import { setError, setLoading } from 'state/reducers/auth.reducers';
import { PARTIAL_URL, SERVER_URLS, apiFunctionsEnum } from 'constants/api.constants';
import { ReportType } from 'constants/reports.constants';
import { errorMessages, timing } from 'constants/ui.constants';
import ApiService from './api.service';
import { store } from 'state/store';
import { globalEnvironment } from 'constants/global.constants';
import * as XLSX from 'xlsx';
import { DashboardCategories, IFilterType } from 'constants/balance.constants';
import _ from 'lodash';
import { format } from 'date-fns';

const getToken = () => {
	return `${store.getState().auth.token}`;
};

export function getReportByType(
	type: string,
	dateFrom: string | null,
	dateTo: string | null,
	companyId?: number | null,
	isDetailed?: boolean
) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setError(null));
		let API: any;
		switch (type) {
			case 'Beneficiaries':
				API = apiFunctionsEnum.GET_BENEFICIARIES_REPORT;
				break;
			case ReportType.BeneficiaryOnboarding:
				API = apiFunctionsEnum.GET_BENEFICIARY_ONBOARDING_REPORT;
				break;
			case 'Transactions':
				API = apiFunctionsEnum.GET_TRANSACTIONS_REPORT;
				break;
			case 'Companies':
				API = apiFunctionsEnum.GET_COMPANIES_REPORT;
				break;
			case 'Refunds':
				API = apiFunctionsEnum.GET_REFUNDS_REPORT;
				break;
			case 'PaymentFees':
				API = apiFunctionsEnum.GET_PAYMENT_FEES_REPORT;
				break;
			case 'ConversionFees':
				API = apiFunctionsEnum.GET_CONVERSION_FEES_REPORT;
				break;
			case 'AccountActivities':
				API = apiFunctionsEnum.GET_ACCOUNT_ACTIVITIES_REPORT;
				break;
		}
		await ApiService.getInstance()
			.execute(API, {
				reportName: type,
				reportFormat: 'xlsx',
				dateFrom,
				dateTo,
				companyId,
				isDetailed,
			})
			.then((response: any) => {
				const result = response.data.result;
				const ws = result.length ? XLSX.utils.json_to_sheet(result) : XLSX.utils.aoa_to_sheet([['No data']]);
				const wb = XLSX.utils.book_new();

				// Set RTL direction for specific reports at workbook level
				if (
					type === ReportType.PaymentFees ||
					type === ReportType.ConversionFees ||
					type === ReportType.BeneficiaryOnboarding
				) {
					wb.Workbook = {
						Views: [{ RTL: true }],
					};
				}

				XLSX.utils.book_append_sheet(wb, ws);
				const fileSuffix = '.xlsx';
				const reportFileName =
					type === ReportType.BeneficiaryOnboarding ? 'דוח קליטת מוטבים' : type;
				XLSX.writeFile(
					wb,
					dateFrom && dateTo
						? `${dateFrom}-${dateTo}-${reportFileName}${fileSuffix}`
						: `from-all-dates-${reportFileName}${fileSuffix}`
				);
			})
			.catch((error: any) => {
				dispatch(setError(errorMessages.generalError));
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getBalancesAndFeesXlsx(
	type: string,
	filters: IFilterType,
	countries: any,
	isDetailed?: boolean,
	plan?: any
) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setError(null));
		let API: any;
		let resultName: any;
		let extraParamName = 'isDetailed';
		let extraParam: any = isDetailed;
		switch (type) {
			case DashboardCategories.CUSTOMER_BALANCES:
				API = apiFunctionsEnum.GET_ACCOUNT_BALANCE;
				resultName = 'accountBalance';
				break;
			case DashboardCategories.PENDING_BALANCE:
				API = apiFunctionsEnum.GET_PENDING_BALANCE;
				resultName = 'pendingBalance';
				break;
			case DashboardCategories.CONVERSION_FEES:
				API = apiFunctionsEnum.GET_CONVERSION_FEES;
				resultName = 'conversionFees';
				break;
			case DashboardCategories.PAYMENT_FEES:
				API = apiFunctionsEnum.GET_PAYMENT_FEES;
				resultName = 'paymentFees';
				break;
			case DashboardCategories.MONTHLY_SUBSCRIPTIONS:
				API = apiFunctionsEnum.GET_PLAN_FEES;
				resultName = 'monthlySubscriptions';
				break;
			case DashboardCategories.TOTAL_REVENUE:
				API = apiFunctionsEnum.GET_TOTAL_REVENUE_BY_COMPANY;
				resultName = 'totalRevenueByCompany';
				break;
			case DashboardCategories.MONTHLY_SUBSCRIPTIONS_BY_CUSTOMERS:
				API = apiFunctionsEnum.GET_PLAN_FEES_BY_COMPANY;
				resultName = 'companyFeesForEachPlan';
				extraParamName = 'planName';
				extraParam = plan.toLowerCase();
				break;
		}

		await ApiService.getInstance()
			.execute(API, { filters, [extraParamName]: extraParam })
			.then((response: any) => {
				let results = response.data[resultName];
				if (type === 'monthlySubscriptions') {
					results = results.map((row: any) => {
						return _.omit(row, 'paymentAndConversionFeesByPlan');
					});
				}
				if (type === DashboardCategories.PAYMENT_FEES) {
					results = _.map(results, (result) => {
						const country = _.find(countries, { id: result.countryId }).name;
						const bankAccountCountry = _.find(countries, { id: result.bankAccountCountryId }).name;
						return {
							..._.omit(result, ['countryId', 'bankAccountCountryId']),
							country,
							bankAccountCountry,
						};
					});
				}

				const ws = results.length ? XLSX.utils.json_to_sheet(results) : XLSX.utils.aoa_to_sheet([['No data']]);
				const wb = XLSX.utils.book_new();
				XLSX.utils.book_append_sheet(wb, ws);
				const fileSuffix = '.xlsx';
				XLSX.writeFile(
					wb,
					`${type}-${format(filters?.datesRange?.endDate, timing.DATE_FOR_SERVICE)}${fileSuffix}`
				);
			})
			.catch((error: any) => {
				console.log(error);
				dispatch(setError(errorMessages.generalError));
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getBalanceByCustomersDataXlsx(type: string, filters: IFilterType, isDetailed?: boolean) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setError(null));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_ACCOUNT_BALANCE, { filters, isDetailed })
			.then((response: any) => {
				const result = response.data.accountBalance;
				const res = result.map((row: any) => {
					const balance = _.keyBy(row.balances, 'currencyCode');
					const list = _.mapValues(balance, (vals: any) => vals.amount);
					return {
						..._.omit(row, 'balances'),
						...list,
					};
				});

				const ws = res.length ? XLSX.utils.json_to_sheet(res) : XLSX.utils.aoa_to_sheet([['No data']]);
				const wb = XLSX.utils.book_new();
				XLSX.utils.book_append_sheet(wb, ws);
				const fileSuffix = '.xlsx';
				XLSX.writeFile(
					wb,
					`${type}-${format(filters?.datesRange?.endDate, timing.DATE_FOR_SERVICE)}${fileSuffix}`
				);
			})
			.catch((error: any) => {
				console.log(error);
				dispatch(setError(errorMessages.generalError));
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getBalancesReport(
	type: string,
	dateFrom: string | null,
	dateTo: string | null,
	companyId?: number | null
) {
	const baseUrl = `${SERVER_URLS[globalEnvironment]}${PARTIAL_URL}`;
	return (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setError(null));
		window.open(
			baseUrl +
				'/get-report/report/balances' +
				`?reportName=${type}&reportFormat=pdf&dateFrom=${dateFrom}&dateTo=${dateTo}&companyId=${companyId}&tempCode=${getToken()}`,
			'_blank'
		);
		dispatch(setLoading(false));
	};
}
